/**
 * The six axis views, and what they are honestly called.
 *
 * A reconstruction has no idea which of its sides is the front. The solver
 * picked an orientation that made the maths work, and nothing downstream
 * measured the object's own semantics - so naming these "Ön" and "Arka" would
 * assert something this product does not know.
 *
 * They are named for the direction the camera looks *from*, in the viewer's
 * own coordinate frame, which is a fact rather than a claim. Somebody who has
 * calibrated the scale and wants a specific face still gets there in one
 * click; they are just not told it is the front.
 */

import type { Camera, Vec3 } from "./viewerCamera";

export interface ViewPreset {
  key: string;
  label: string;
  /** Azimuth around the vertical axis, radians. */
  theta: number;
  /** Elevation, radians. Clamped short of the poles by the camera itself. */
  phi: number;
}

/** Just short of vertical: at exactly 90 degrees the up vector degenerates. */
const POLE = Math.PI / 2 - 0.01;

export const VIEW_PRESETS: readonly ViewPreset[] = [
  { key: "front", label: "Önden", theta: 0, phi: 0 },
  { key: "right", label: "Sağdan", theta: Math.PI / 2, phi: 0 },
  { key: "back", label: "Arkadan", theta: Math.PI, phi: 0 },
  { key: "left", label: "Soldan", theta: -Math.PI / 2, phi: 0 },
  { key: "top", label: "Üstten", theta: 0, phi: POLE },
  { key: "bottom", label: "Alttan", theta: 0, phi: -POLE },
  // The one that is not an axis: a three-quarter view is what most people
  // actually want to look at a shape from, and it is the view the model opens
  // at, so having it as a button makes the opening view recoverable.
  { key: "iso", label: "İzometrik", theta: 0.6, phi: 0.3 },
];

/**
 * Move a camera onto a preset without changing how far away it is.
 *
 * Distance and target are deliberately preserved. A preset that also re-fitted
 * would throw away a zoom somebody set deliberately to look at one corner,
 * and "look at this from the left" is not a request to stop looking closely.
 */
export function applyPreset(camera: Camera, preset: ViewPreset): Camera {
  return {
    ...camera,
    theta: preset.theta,
    phi: preset.phi,
    target: [...camera.target] as Vec3,
  };
}
